import {
  type Attributes,
  type Span,
  SpanStatusCode,
  context,
  propagation,
  trace,
} from '@opentelemetry/api';

export interface PropagationContext {
  get(name: string): string | undefined;
}

interface StartSpanOptions {
  name: string;
  attributes?: Attributes;
  context?: PropagationContext;
}

export function startSpan<T>(
  options: StartSpanOptions,
  fn: (span: Span) => T,
): T {
  // If there's an active trace, use it.
  const tracer = trace.getTracer('pleyr-web');
  if (trace.getActiveSpan()) {
    return tracer.startActiveSpan(options.name, (s) =>
      executeWithSpan(s, options.attributes ?? {}, fn),
    );
  }

  // If there's a trace in the context, attach to it.
  const ctx = getContext(options.context);
  if (ctx) {
    return context.with(ctx, () =>
      tracer.startActiveSpan(options.name, (s) =>
        executeWithSpan(s, options.attributes ?? {}, fn),
      ),
    );
  }

  // Otherwise start a new trace.
  return tracer.startActiveSpan(options.name, (s) =>
    executeWithSpan(s, options.attributes ?? {}, fn),
  );
}

export function getContext(propCtx?: PropagationContext) {
  if (trace.getActiveSpan()) {
    return context.active();
  }

  // Browser support
  if (!propCtx && 'document' in globalThis) {
    propCtx = {
      get: (name) =>
        document
          .querySelector(`meta[name="${name}"]`)
          ?.getAttribute('content') ?? undefined,
    };
  }

  const traceparent = propCtx?.get('traceparent');
  if (!traceparent) return;

  const tracestate = propCtx?.get('tracestate');
  const headers: Record<string, string> = { traceparent };
  if (tracestate) headers.tracestate = tracestate;

  return propagation.extract(context.active(), headers);
}

function executeWithSpan<T>(
  span: Span,
  attrs: Attributes,
  fn: (span: Span) => T,
) {
  span.setAttributes(attrs);

  let didEnd = false;
  try {
    const r = fn(span);
    if (r instanceof Promise) {
      didEnd = true;
      return r.finally(() => span.end()) as T;
    }
    return r;
  } finally {
    if (!didEnd) {
      span.end();
    }
  }
}

export function captureException(
  error: unknown,
  attrs: Attributes = {},
): string | undefined {
  const span = trace.getActiveSpan();
  if (!span) return;

  span.recordException(error as Error);
  span.setStatus({ code: SpanStatusCode.ERROR });
  span.setAttributes(attrs);
  return span.spanContext().spanId;
}

export function getTracePropagationHeaders(): Record<string, string> {
  const span = trace.getActiveSpan();
  if (!span) return {};

  const headers: Record<string, string> = {};
  propagation.inject(context.active(), headers);
  return headers;
}
